import { useState, useEffect, useCallback } from 'react';
import { HelpCircle, Plus, ArrowLeft, Send, Clock, CheckCircle, AlertTriangle, Archive, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input, Select, Textarea } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { getTickets, getTicket, createTicket, addTicketMessage } from '../api/support';
import { useAuth } from '../context/AuthContext';

const STATUS_CONFIG = {
  open:        { label: 'Açık',        color: 'bg-amber-100 text-amber-700',  icon: AlertTriangle, iconColor: 'text-amber-500', tooltip: 'Açık - Yanıt bekleniyor' },
  in_progress: { label: 'İnceleniyor', color: 'bg-blue-100 text-blue-700',    icon: RefreshCw,     iconColor: 'text-blue-500',  tooltip: 'İnceleniyor - Destek ekibi ilgileniyor' },
  resolved:    { label: 'Çözüldü',     color: 'bg-green-100 text-green-700',  icon: CheckCircle,   iconColor: 'text-green-500', tooltip: 'Çözüldü' },
  closed:      { label: 'Arşivlendi',  color: 'bg-purple-100 text-purple-600', icon: Archive,      iconColor: 'text-purple-400', tooltip: 'Kapatıldı - Çözümlendi ve arşivlendi' },
};

const PRIORITY_CONFIG = {
  low:    { label: 'Düşük',  color: 'bg-slate-100 text-slate-600' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600' },
  high:   { label: 'Yüksek', color: 'bg-red-100 text-red-600' },
};

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await getTickets(params);
      setTickets(data.tickets);
    } catch {}
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const openDetail = async (id) => {
    setSelected(id);
    try {
      const data = await getTicket(id);
      setDetail(data.ticket);
      setMessages(data.messages);
    } catch {}
  };

  const handleSendMessage = async () => {
    if (!newMsg.trim() || !selected) return;
    setSending(true);
    try {
      await addTicketMessage(selected, { message: newMsg });
      setNewMsg('');
      const data = await getTicket(selected);
      setMessages(data.messages);
      setDetail(data.ticket);
    } catch {}
    setSending(false);
  };

  // Ticket listesi
  if (selected && detail) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => { setSelected(null); setDetail(null); loadTickets(); }}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
          <ArrowLeft size={16} /> Geri
        </button>

        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-800">{detail.subject}</h2>
              <p className="text-sm text-slate-500 mt-1">{fmtDate(detail.created_at)}</p>
            </div>
            <div className="flex gap-2">
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${PRIORITY_CONFIG[detail.priority]?.color}`}>
                {PRIORITY_CONFIG[detail.priority]?.label}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_CONFIG[detail.status]?.color}`}>
                {STATUS_CONFIG[detail.status]?.label}
              </span>
            </div>
          </div>
          <div className="mt-4 p-4 bg-slate-50 rounded-lg text-sm text-slate-700 whitespace-pre-wrap">
            {detail.message}
          </div>
        </div>

        {/* Mesajlar */}
        <div className="space-y-3 mb-4">
          {messages.map(m => (
            <div key={m.id} className={`p-4 rounded-xl text-sm ${
              m.sender_type === 'admin'
                ? 'bg-green-50 border border-green-200 ml-8'
                : 'bg-white border border-slate-200 mr-8'
            }`}>
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-slate-800">
                  {m.sender_name}
                  {m.sender_type === 'admin' && <span className="ml-1 text-xs text-green-600">(Destek)</span>}
                </span>
                <span className="text-xs text-slate-400">{fmtDate(m.created_at)}</span>
              </div>
              <p className="text-slate-700 whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
        </div>

        {/* Yanıt yaz */}
        {detail.status !== 'closed' && (
          <div className="flex gap-2">
            <input
              className="flex-1 border border-slate-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-blue-300"
              placeholder="Yanıtınızı yazın..."
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
            />
            <Button onClick={handleSendMessage} loading={sending} disabled={!newMsg.trim()}>
              <Send size={16} />
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <HelpCircle size={22} /> Destek
          </h1>
          <p className="text-sm text-slate-500 mt-1">Sorularınız ve talepleriniz</p>
        </div>
        <div className="flex gap-3">
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-40">
            <option value="">Tüm Durumlar</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </Select>
          <Button onClick={() => setShowNew(true)}>
            <Plus size={16} /> Yeni Talep
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <HelpCircle size={48} className="mx-auto mb-3 opacity-30" />
          <p>Henüz destek talebi yok</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => {
            const sc = STATUS_CONFIG[t.status];
            const Icon = sc?.icon || AlertTriangle;
            return (
              <div key={t.id} onClick={() => openDetail(t.id)}
                className="bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 cursor-pointer transition flex items-center gap-4">
                <Icon size={20} className={sc?.iconColor || 'text-slate-400'} title={sc?.tooltip} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">{t.subject}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t.user_name} &middot; {fmtDate(t.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {t.message_count > 0 && (
                    <span className="text-xs text-slate-400">{t.message_count} mesaj</span>
                  )}
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${PRIORITY_CONFIG[t.priority]?.color}`}>
                    {PRIORITY_CONFIG[t.priority]?.label}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${sc?.color}`} title={sc?.tooltip}>
                    {sc?.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Yeni Ticket Modal */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="Yeni Destek Talebi" size="md">
        <NewTicketForm onSuccess={() => { setShowNew(false); loadTickets(); }} onCancel={() => setShowNew(false)} />
      </Modal>
    </div>
  );
}

function NewTicketForm({ onSuccess, onCancel }) {
  const [form, setForm] = useState({ subject: '', message: '', priority: 'normal' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      setError('Konu ve mesaj gerekli');
      return;
    }
    setLoading(true);
    try {
      await createTicket(form);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Bir hata oluştu');
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input label="Konu *" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Destek talebinizin konusu" />
      <Select label="Öncelik" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
        <option value="low">Düşük</option>
        <option value="normal">Normal</option>
        <option value="high">Yüksek</option>
      </Select>
      <Textarea label="Mesaj *" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={5} placeholder="Sorununuzu detaylı anlatın..." />
      {error && <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg">{error}</div>}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>İptal</Button>
        <Button type="submit" loading={loading}>Gönder</Button>
      </div>
    </form>
  );
}
